import React, { useRef, useCallback, useEffect, useState, type ChangeEvent } from 'react';
import { AsyncSelect } from '@atlaskit/select';
import { useIntl } from '@atlassian/jira-intl';
import { defaultSelectStyles } from '@atlassian/jira-issue-field-select-base/src/ui/react-select-styles/styled.tsx';
import debouncePromise from '@atlassian/jira-platform-debounce-promise/src/ui/debounced-promise/index.tsx';
import { useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { useAccountId } from '@atlassian/jira-tenant-context-controller/src/components/account-id/index.tsx';
import type { AssetOption, AssetType } from '../../common/types';
import messages from './messages';
import { fetchAssets, fetchFieldFilterNames } from './utils';

export const LOAD_OPTIONS_DEBOUNCE_TIMER = 300;

export type Props = {
	isInvalid?: boolean;
	isDisabled?: boolean;
	isDropdownMenuFixedAndLayered?: boolean;
	issueTypeId?: string;
	autoCompleteUrl: string;
	value: AssetOption[];
	fieldId: string;
	placeholder?: string;
	projectId: string;
	fetchSuggestionsOnFocus?: boolean;
	onChange: (value: AssetOption[]) => void;
	onCloseMenuOnScroll?: (e: ChangeEvent<HTMLElement>) => boolean;
	classNamePrefix?: string;
	ariaLabelledBy?: string;
};

// react-select with isMulti can return both null or an empty array for onChange depending on how
// it was cleared. Normalize this to an empty array https://github.com/JedWatson/react-select/issues/3632
const normalizeOnChangeValues =
	(onChange: (value: AssetOption[]) => void) => (updatedValues: AssetOption[] | null) =>
		onChange(updatedValues || []);

const AssetsEdit = (props: Props) => {
	const {
		fieldId,
		value,
		onChange,
		autoCompleteUrl,
		isInvalid,
		placeholder = '',
		isDropdownMenuFixedAndLayered,
		onCloseMenuOnScroll,
		isDisabled,
		issueTypeId,
		projectId,
		fetchSuggestionsOnFocus = false,
		classNamePrefix,
		ariaLabelledBy,
	} = props;
	const { formatMessage } = useIntl();
	const accountId = useAccountId();
	const [assetFilterName, setAssetFilterName] = useState<string[]>([]);
	const [, setAssetFilterConfigError] = useState(false);
	const [lastFetchFailed, setLastFetchFailed] = useState<boolean>(false);
	const [defaultOptions, setDefaultOptions] = useState<AssetOption[]>([]);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const shouldFetchDefaultOptions = useRef<boolean>(true);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	/*
	 * 1. Use customFieldId (eg. customfield_10085) to get asset filter contextId (eg. 10025)
	 * 2. Use asset filter contextId to get asset filter name (eg. com.atlassian.servicedesk.connect.test-bed/mac.type.id)
	 * 3. Use asset filter name for doing query in fetchAssets when user types in name of asset
	 * */
	useEffect(() => {
		fetchFieldFilterNames(createAnalyticsEvent({}), fieldId, projectId, issueTypeId)
			.then((result: AssetType[]) => {
				setAssetFilterName(result.map((res) => `${res.appKey}/${res.originId}`));
			})
			.catch(() => {
				setAssetFilterConfigError(true);
			});
	}, [fieldId, projectId, issueTypeId, createAnalyticsEvent]);

	const searchAssets = useCallback(
		async (query?: string): Promise<AssetOption[]> => {
			setLastFetchFailed(false);
			return fetchAssets(
				query,
				autoCompleteUrl,
				accountId,
				assetFilterName,
				createAnalyticsEvent({}),
			).catch(() => {
				setLastFetchFailed(true);
				return [];
			});
		},
		[autoCompleteUrl, accountId, assetFilterName, createAnalyticsEvent],
	);

	const onFocus = async () => {
		if (
			shouldFetchDefaultOptions &&
			defaultOptions &&
			shouldFetchDefaultOptions.current &&
			!defaultOptions.length
		) {
			shouldFetchDefaultOptions.current = false;
			setIsLoading && setIsLoading(true);
			const options = await searchAssets();
			setDefaultOptions && setDefaultOptions(options);
			setIsLoading && setIsLoading(false);
		}
	};
	const loadOptionsDebounced = debouncePromise(searchAssets, LOAD_OPTIONS_DEBOUNCE_TIMER);

	const noOptionsMessage = useCallback(
		() => formatMessage(lastFetchFailed ? messages.failedFetch : messages.noMatches),
		[formatMessage, lastFetchFailed],
	);

	const defaultOptionsProps = () => {
		if (fetchSuggestionsOnFocus) {
			return { onFocus, isLoading, defaultOptions };
		}
		return {};
	};

	return (
		<AsyncSelect
			fieldId={fieldId}
			loadOptions={loadOptionsDebounced}
			// @ts-expect-error - TS2322 - Type '(updatedValues: AssetOption[] | null) => void' is not assignable to type '(value: OptionsType<AssetOption>, action: ActionMeta<AssetOption>) => void'.
			onChange={normalizeOnChangeValues(onChange)}
			autoFocus={false}
			{...defaultOptionsProps()}
			isClearable={Boolean(value)}
			isMulti
			value={value}
			// @ts-expect-error - TS2322 - Type '"error" | null' is not assignable to type 'ValidationState | undefined'.
			validationState={isInvalid === true ? 'error' : null}
			noOptionsMessage={noOptionsMessage}
			placeholder={placeholder}
			// @ts-expect-error - TS2322 - Type '((e: ChangeEvent<HTMLElement>) => boolean) | undefined' is not assignable to type 'boolean | EventListener | undefined'.
			closeMenuOnScroll={onCloseMenuOnScroll}
			isDisabled={isDisabled}
			menuPosition={isDropdownMenuFixedAndLayered === true ? 'fixed' : undefined}
			styles={defaultSelectStyles}
			classNamePrefix={classNamePrefix}
			aria-labelledby={ariaLabelledBy}
		/>
	);
};

export default AssetsEdit;
